<template>
  <div>
    <transition name="slide-up">
      <div
        v-if="showPrompt"
        class="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-2xl rounded-t-2xl sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md sm:rounded-2xl"
      >
      <div class="flex items-start justify-between">
        <div class="flex-1 pr-4">
          <div class="flex items-center mb-2">
            <svg class="w-8 h-8 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
            <h3 class="text-lg font-bold">Install App</h3>
          </div>
          <p class="text-sm text-white/90 mb-4">
            Install SV Shop for quick access, offline browsing, and a better shopping experience.
          </p>
          <div class="flex gap-2">
            <button
              @click="install"
              class="flex-1 bg-white text-primary-600 px-4 py-2.5 rounded-xl font-semibold hover:bg-white/90 transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-primary-600"
            >
              Install
            </button>
            <button
              @click="dismiss"
              class="px-4 py-2.5 rounded-xl font-semibold hover:bg-white/10 transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              Not Now
            </button>
          </div>
        </div>
        <button
          @click="dismiss"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
          aria-label="Close install prompt"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        </div>
      </div>
    </transition>

    <!-- Update notification -->
    <transition name="slide-up">
      <div
        v-if="showUpdateNotification"
        class="fixed bottom-20 left-0 right-0 z-50 p-4 sm:bottom-24 sm:left-4 sm:right-auto sm:max-w-md"
      >
        <div class="bg-white rounded-2xl shadow-2xl p-4 border border-gray-200">
          <div class="flex items-start justify-between">
            <div class="flex-1 pr-4">
              <div class="flex items-center mb-2">
                <svg class="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                <h3 class="text-base font-bold text-gray-900">Update Available</h3>
              </div>
              <p class="text-sm text-gray-600 mb-3">
                A new version of the app is available. Reload to update.
              </p>
              <button
                @click="reload"
                class="w-full bg-primary-500 text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-primary-600 transition-colors min-h-[44px]"
              >
                Reload Now
              </button>
            </div>
            <button
              @click="hideUpdate"
              class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Dismiss update notification"
            >
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import logger from '@/utils/logger'
import { mapState, mapActions } from 'vuex'

// localStorage key for tracking when the prompt was last dismissed
const PWA_PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed'
// 30 days in milliseconds
const PROMPT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

export default {
  name: 'PwaInstallPrompt',
  computed: {
    ...mapState({
      showPrompt: state => state.pwa.showInstallPrompt,
      showUpdateNotification: state => state.pwa.showUpdateNotification,
      isInstalled: state => state.pwa.isInstalled
    })
  },
  mounted() {
    this.setupPwaListeners()
    this.checkIfAlreadyInstalled()
  },
  methods: {
    ...mapActions([
      'captureInstallPrompt',
      'installPwa',
      'dismissInstallPrompt',
      'hideUpdateNotification',
      'showInstallPrompt'
    ]),

    /**
     * Check if the prompt should be shown based on 30-day cooldown
     * Returns true if prompt can be shown, false if still in cooldown period
     */
    canShowPrompt() {
      try {
        const dismissedAt = localStorage.getItem(PWA_PROMPT_DISMISSED_KEY)
        if (!dismissedAt) {
          // Never dismissed, can show
          return true
        }

        const dismissedTime = parseInt(dismissedAt, 10)
        const now = Date.now()
        const elapsed = now - dismissedTime

        if (elapsed >= PROMPT_COOLDOWN_MS) {
          // 30 days have passed, can show again
          logger.debug('[PWA] 30-day cooldown expired, can show prompt')
          return true
        }

        // Still in cooldown period
        const daysRemaining = Math.ceil((PROMPT_COOLDOWN_MS - elapsed) / (24 * 60 * 60 * 1000))
        logger.debug(`[PWA] Prompt in cooldown, ${daysRemaining} days remaining`)
        return false
      } catch (error) {
        // localStorage not available, default to showing
        logger.warn('[PWA] localStorage not available:', error)
        return true
      }
    },

    /**
     * Record that the user dismissed the prompt (starts 30-day cooldown)
     */
    recordPromptDismissal() {
      try {
        localStorage.setItem(PWA_PROMPT_DISMISSED_KEY, Date.now().toString())
        logger.debug('[PWA] Recorded prompt dismissal, will not show for 30 days')
      } catch (error) {
        logger.warn('[PWA] Failed to save dismissal to localStorage:', error)
      }
    },

    setupPwaListeners() {
      // Listen for beforeinstallprompt event
      window.addEventListener('beforeinstallprompt', (e) => {
        logger.debug('[PWA] beforeinstallprompt event fired')
        e.preventDefault()
        this.captureInstallPrompt(e)

        // Auto-show install prompt after a delay (if not in cooldown)
        setTimeout(() => {
          if (!this.isInstalled && this.canShowPrompt()) {
            this.showInstallPrompt()
          }
        }, 3000) // Show after 3 seconds
      })

      // Listen for app installed event
      window.addEventListener('appinstalled', () => {
        logger.debug('[PWA] App installed successfully')
        this.dismissInstallPrompt()
      })

      // Check if running in standalone mode (already installed)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        logger.debug('[PWA] App is running in standalone mode')
      }
    },

    checkIfAlreadyInstalled() {
      // Check if app is already installed (running in standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIosStandalone = window.navigator.standalone === true

      if (isStandalone || isIosStandalone) {
        this.$store.commit('setPwaInstalled', true)
      }
    },

    async install() {
      const result = await this.installPwa()
      if (result.success && result.installed) {
        logger.debug('[PWA] App installation completed')
        // Clear the dismissal record since user installed
        try {
          localStorage.removeItem(PWA_PROMPT_DISMISSED_KEY)
        } catch (error) {
          // Ignore localStorage errors
        }
      }
    },

    dismiss() {
      // Record dismissal for 30-day cooldown
      this.recordPromptDismissal()
      this.dismissInstallPrompt()
    },

    reload() {
      window.location.reload()
    },

    hideUpdate() {
      this.hideUpdateNotification()
    }
  }
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

/* Vue 2.7 uses `-enter`/`-leave`; `-enter-from`/`-leave-from` are Vue 3 names. Include both. */
.slide-up-enter-from,
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from,
.slide-up-leave {
  transform: translateY(0);
  opacity: 1;
}
</style>
